<template>
	<basic-layout id="copy-right-claim-1">
		<template #content>
			<div class="copyright-form-root">
				<header class="form-hero text-center">
					<h1 class="form-hero__title">Copyright claim</h1>
					<p class="form-hero__lead">
						Report content you believe infringes your rights. Your report is reviewed by our team, please
						provide accurate links and details so we can help quickly.
					</p>
					<div class="form-hero__chips d-flex flex-wrap justify-center">
						<span class="form-hero__chip">
							<v-icon small color="primary" class="mr-1">schedule</v-icon>
							Fast review
						</span>
						<span class="form-hero__chip">
							<v-icon small color="primary" class="mr-1">lock</v-icon>
							Secure submission
						</span>
						<span class="form-hero__chip">
							<v-icon small color="primary" class="mr-1">verified_user</v-icon>
							Legally compliant
						</span>
					</div>
					<div class="form-hero__progress-wrap" aria-label="Form completion progress">
						<div class="form-hero__steps">
							<span
								v-for="(done, i) in sectionComplete"
								:key="i"
								class="form-hero__step-dot"
								:class="{
									'form-hero__step-dot--done': done,
									'form-hero__step-dot--active': !done && i === sectionComplete.indexOf(false)
								}"
								:aria-label="`Section ${i + 1}: ${done ? 'complete' : 'incomplete'}`"
							>
								<v-icon v-if="done" x-small color="white" style="font-size:9px;">check</v-icon>
							</span>
						</div>
						<p class="form-hero__progress-label">
							{{ completedCount }} of 7 sections complete
						</p>
					</div>
				</header>

				<!-- 1. Claim -->
				<v-card class="copyright-section copyright-section--step" tag="section" outlined>
					<div class="section-heading">
						<div class="section-heading__badge" aria-hidden="true">1</div>
						<div class="section-heading__text">
							<h2 class="section-heading__title">Your claim</h2>
							<p class="section-heading__desc">Choose the option that best describes your concern.</p>
						</div>
					</div>
					<v-divider class="mb-6" />
					<claim-title>What is your claim? *</claim-title>
					<v-layout wrap>
						<v-flex xs12>
							<v-select
								:items="claimItems"
								v-model="claimType"
								item-text="text"
								item-value="value"
								label="Type of claim"
								outlined
								hide-details
							/>
						</v-flex>
						<v-flex xs12 v-if="claimType === 3">
							<v-text-field
								label="Please add information about your issue *"
								v-model="otherClaimType"
								:error-messages="fieldErrors.otherClaimType"
								outlined
								:hide-details="!fieldErrors.otherClaimType.length"
							/>
						</v-flex>
					</v-layout>
				</v-card>

				<!-- 2. Links -->
				<div class="section-connector" :class="{'section-connector--done': sectionComplete[0]}" aria-hidden="true"></div>
				<v-card class="copyright-section copyright-section--step" tag="section" outlined>
					<div class="section-heading">
						<div class="section-heading__badge" aria-hidden="true">2</div>
						<div class="section-heading__text">
							<h2 class="section-heading__title">Content links</h2>
							<p class="section-heading__desc">
								Please submit link(s) to specific posts only. Profile links are not supported.
							</p>
						</div>
					</div>
					<v-divider class="mb-6" />
					<claim-title>
						Link to the content *
						<template #hint>
							Press Enter after each URL to add another link.
						</template>
					</claim-title>
					<div class="content-links-field">
						<v-combobox
							data-cy="content-link"
							multiple
							v-model="links"
							label="Post URLs"
							hint="Press Enter after each link"
							persistent-hint
							append-icon
							chips
							deletable-chips
							outlined
						/>
					</div>
					<div data-cy="error" v-if="linkErrors.length" class="link-error-banner mt-2">
						<v-icon small color="error" class="mr-2">error_outline</v-icon>
						<span>{{ linkErrors.join(" · ") }}</span>
					</div>
				</v-card>

				<!-- 3. Impact -->
				<div class="section-connector" :class="{'section-connector--done': sectionComplete[1]}" aria-hidden="true"></div>
				<v-card class="copyright-section copyright-section--step" tag="section" outlined>
					<div class="section-heading">
						<div class="section-heading__badge" aria-hidden="true">3</div>
						<div class="section-heading__text">
							<h2 class="section-heading__title">Impact &amp; harm</h2>
							<p class="section-heading__desc">Help us understand who is affected and how.</p>
						</div>
					</div>
					<v-divider class="mb-6" />
					<claim-title>Who is affected? *</claim-title>
					<v-layout wrap>
						<v-flex xs12>
							<v-select
								:items="affectedWho"
								v-model="affectedPerson"
								item-text="text"
								item-value="value"
								label="Affected party"
								outlined
								hide-details
							/>
						</v-flex>
						<v-flex xs12 v-if="affectedPerson === 3 || affectedPerson === 4">
							<v-text-field
								label="Please add information about the affectee *"
								v-model="otherAffectedPerson"
								:error-messages="fieldErrors.otherAffectedPerson"
								outlined
								:hide-details="!fieldErrors.otherAffectedPerson.length"
							/>
						</v-flex>
					</v-layout>

					<div class="subsection-label mt-8 mb-2">How does it affect them? *</div>
					<claim-title>It’s an unauthorised use of:</claim-title>
					<v-layout wrap>
						<v-flex xs12>
							<v-select
								:items="unauthorisedUseReasons"
								v-model="unauthorisedUseReason"
								item-text="text"
								item-value="value"
								outlined
								hide-details
							/>
						</v-flex>
						<v-flex xs12 v-if="unauthorisedUseReason === 7">
							<v-text-field
								label="Please add more information about unautorised use *"
								v-model="otherUnauthorisedUseReason"
								:error-messages="fieldErrors.otherUnauthorisedUseReason"
								outlined
								:hide-details="!fieldErrors.otherUnauthorisedUseReason.length"
							/>
						</v-flex>
					</v-layout>

					<div class="mt-6">
						<claim-title>It hampers the right holder’s ability to:</claim-title>
						<v-layout wrap>
							<v-flex xs12>
								<v-select
									:items="abilityHamperReasons"
									v-model="abilityHamperReason"
									item-text="text"
									item-value="value"
									outlined
									hide-details
								/>
							</v-flex>
							<v-flex xs12 v-if="abilityHamperReason === 6">
								<v-text-field
									label="Please add more information *"
									v-model="otherAbilityHamperReason"
									:error-messages="fieldErrors.otherAbilityHamperReason"
									outlined
									:hide-details="!fieldErrors.otherAbilityHamperReason.length"
								/>
							</v-flex>
						</v-layout>
					</div>

					<div class="mt-6">
						<claim-title>How else does this amount to infringement?</claim-title>
						<v-text-field
							label="Additional context (optional)"
							v-model="otherInfringementReason"
							:error-messages="fieldErrors.otherInfringementReason"
							outlined
							:hide-details="!fieldErrors.otherInfringementReason.length"
						/>
					</div>
				</v-card>

				<!-- 4. Identity -->
				<div class="section-connector" :class="{'section-connector--done': sectionComplete[2]}" aria-hidden="true"></div>
				<v-card class="copyright-section copyright-section--step" tag="section" outlined>
					<div class="section-heading">
						<div class="section-heading__badge" aria-hidden="true">4</div>
						<div class="section-heading__text">
							<h2 class="section-heading__title">Who is filing</h2>
							<p class="section-heading__desc">We use this to verify authority to submit this report.</p>
						</div>
					</div>
					<v-divider class="mb-6" />
					<v-layout wrap>
						<v-flex xs12 md6 class="pr-md-3">
							<v-text-field
								data-cy="org-name"
								label="You / your organisation name *"
								v-model="orgName"
								:error-messages="fieldErrors.orgName"
								outlined
								:hide-details="!fieldErrors.orgName.length"
							/>
						</v-flex>
						<v-flex xs12 md6 class="pl-md-3">
							<v-text-field
								label="Your designation"
								v-model="designation"
								:error-messages="fieldErrors.designation"
								outlined
								:hide-details="!fieldErrors.designation.length"
							/>
						</v-flex>
						<v-flex xs12 md6 class="pr-md-3">
							<v-text-field
								label="On behalf of"
								v-model="onBehalf"
								:error-messages="fieldErrors.onBehalf"
								outlined
								:hide-details="!fieldErrors.onBehalf.length"
							/>
						</v-flex>
						<v-flex xs12 md6 class="pl-md-3">
							<v-select
								data-cy="relation-with-owner"
								label="Relationship with copyright owner *"
								:items="relationWithOwners"
								v-model="relationWithOwner"
								item-text="text"
								item-value="value"
								outlined
								hide-details
							/>
						</v-flex>
						<v-flex xs12 v-if="relationWithOwner === 2">
							<v-text-field
								label="Describe your relationship with the owner *"
								v-model="otherRelationWithOwner"
								:error-messages="fieldErrors.otherRelationWithOwner"
								outlined
								:hide-details="!fieldErrors.otherRelationWithOwner.length"
							/>
						</v-flex>
					</v-layout>
				</v-card>

				<!-- 5. Contact -->
				<div class="section-connector" :class="{'section-connector--done': sectionComplete[3]}" aria-hidden="true"></div>
				<v-card class="copyright-section copyright-section--step" tag="section" outlined>
					<div class="section-heading">
						<div class="section-heading__badge" aria-hidden="true">5</div>
						<div class="section-heading__text">
							<h2 class="section-heading__title">Contact details</h2>
							<p class="section-heading__desc">So we can reach you about this report if needed.</p>
						</div>
					</div>
					<v-divider class="mb-6" />
					<v-layout wrap>
						<v-flex xs12 md6 class="pr-md-3">
							<v-text-field
								data-cy="email"
								label="Email *"
								v-model="email"
								:error-messages="fieldErrors.email"
								outlined
								:hide-details="!fieldErrors.email.length"
								type="email"
								autocomplete="email"
							/>
						</v-flex>
						<v-flex xs12 md6 class="pl-md-3">
							<v-text-field
								label="Phone"
								v-model="phone"
								:error-messages="fieldErrors.phone"
								outlined
								:hide-details="!fieldErrors.phone.length"
								autocomplete="tel"
							/>
						</v-flex>
						<v-flex xs12 md6 class="pr-md-3">
							<v-text-field
								data-cy="personal-name"
								label="Personal name *"
								v-model="personalName"
								:error-messages="fieldErrors.personalName"
								outlined
								:hide-details="!fieldErrors.personalName.length"
								autocomplete="name"
							/>
						</v-flex>
						<v-flex xs12 md6 class="pl-md-3">
							<v-text-field
								data-cy="address"
								label="Address *"
								v-model="address"
								:error-messages="fieldErrors.address"
								outlined
								:hide-details="!fieldErrors.address.length"
								autocomplete="street-address"
							/>
						</v-flex>
						<v-flex xs12 md6 class="pr-md-3">
							<v-text-field
								label="Landmark"
								v-model="landmark"
								:error-messages="fieldErrors.landmark"
								outlined
								:hide-details="!fieldErrors.landmark.length"
							/>
						</v-flex>
						<v-flex xs12 md6 class="pl-md-3">
							<v-text-field
								data-cy="city"
								label="City *"
								v-model="city"
								:error-messages="fieldErrors.city"
								outlined
								:hide-details="!fieldErrors.city.length"
								autocomplete="address-level2"
							/>
						</v-flex>
						<v-flex xs12 md6 class="pr-md-3">
							<v-text-field
								data-cy="pincode"
								label="Pincode *"
								v-model="pin"
								:error-messages="fieldErrors.pin"
								outlined
								:hide-details="!fieldErrors.pin.length"
								autocomplete="postal-code"
							/>
						</v-flex>
						<v-flex xs12 md6 class="pl-md-3">
							<v-text-field
								data-cy="country"
								label="Country *"
								v-model="country"
								:error-messages="fieldErrors.country"
								outlined
								:hide-details="!fieldErrors.country.length"
								autocomplete="country-name"
							/>
						</v-flex>
					</v-layout>
				</v-card>

				<!-- 6. Files -->
				<div class="section-connector" :class="{'section-connector--done': sectionComplete[4]}" aria-hidden="true"></div>
				<v-card class="copyright-section copyright-section--step" tag="section" outlined>
					<div class="section-heading">
						<div class="section-heading__badge" aria-hidden="true">6</div>
						<div class="section-heading__text">
							<h2 class="section-heading__title">Supporting files</h2>
							<p class="section-heading__desc">Upload documents that support your claim where relevant.</p>
						</div>
					</div>
					<v-divider class="mb-6" />
					<div class="upload-block mb-8">
						<claim-title>
							Proof of relationship
							<template #hint>
								Contract, letter of authorisation, power of attorney, or similar (if applicable).
							</template>
						</claim-title>
						<media-preview
							:multiple="true"
							@change="onFileChange($event, 'relationProofs')"
							ref="relationProofs"
						/>
					</div>
					<div class="upload-block mb-8">
						<claim-title>
							Verification Document *
							<template #hint>Required. Max 20 MB. Clear scan or photo of your verification document.</template>
						</claim-title>
						<media-preview
							dataCy="upload-signature"
							@change="onFileChange($event, 'signature')"
							ref="signature"
						/>
					</div>
					<div class="upload-block">
						<claim-title>
							Any other details
							<template #hint>Optional additional documents.</template>
						</claim-title>
						<media-preview :multiple="true" @change="onFileChange($event, 'otherFiles')" ref="otherFiles" />
					</div>
				</v-card>

				<!-- 7. Declaration & submit -->
				<div class="section-connector" :class="{'section-connector--done': sectionComplete[5]}" aria-hidden="true"></div>
				<v-card class="copyright-section copyright-section--step copyright-section--legal" tag="section" outlined>
					<div class="section-heading">
						<div class="section-heading__badge section-heading__badge--muted" aria-hidden="true">7</div>
						<div class="section-heading__text">
							<h2 class="section-heading__title">Declaration</h2>
							<p class="section-heading__desc">By submitting, you confirm the following.</p>
						</div>
					</div>
					<v-divider class="mb-6" />
					<ul class="certify-list">
						<li v-for="(item, i) in herebyCertifyPoints" :key="i" class="certify-list__item">
							<div class="certify-list__check" aria-hidden="true"></div>
							<span>{{ item }}</span>
						</li>
					</ul>
				</v-card>

				<v-card class="copyright-submit-panel" outlined>
					<div class="copyright-submit-panel__header">
						<div class="copyright-submit-panel__icon" aria-hidden="true">
							<v-icon color="primary" size="20">verified_user</v-icon>
						</div>
						<p class="copyright-submit-panel__note">
							Complete the security check below, then submit. You can’t send the form until all required fields
							are valid.
						</p>
					</div>
					<vue-recaptcha
						data-cy="recaptcha"
						class="mb-4 copyright-recaptcha"
						ref="recaptcha"
						:sitekey="siteKey"
						type="checkbox"
						@verify="verifyUser"
						@expired="onExpired"
						:loadRecaptchaScript="true"
					/>
					<button
						data-cy="submit-btn"
						type="button"
						class="copyright-submit-btn"
						:class="{ 'copyright-submit-btn--disabled': disabled, 'copyright-submit-btn--loading': loading }"
						:disabled="disabled || loading"
						@click="onSubmitClick"
						:aria-busy="loading"
					>
						<span v-if="loading" class="copyright-submit-btn__spinner" aria-hidden="true"></span>
						<v-icon v-else color="white" size="20" class="mr-2" style="opacity:0.9">gavel</v-icon>
						<span>{{ loading ? 'Submitting…' : 'Submit copyright claim' }}</span>
					</button>
				</v-card>
			</div>
		</template>
	</basic-layout>
</template>

<script>
import BasicLayout from "@/components/basic-layout";
import MediaPreview from "@/components/media-preview";
import VueRecaptcha from "vue-recaptcha";
import ClaimTitle from "@/components/claim-title";

import config from "@/config";
import { findErrorInSharechatLink, claimFieldValidators } from "@/utils";

const initialData = {
	links: [],
	claimType: 1,
	otherClaimType: "",
	affectedPerson: 1,
	otherAffectedPerson: "",
	unauthorisedUseReason: 1,
	otherUnauthorisedUseReason: "",
	abilityHamperReason: 1,
	otherAbilityHamperReason: "",
	otherInfringementReason: "",
	orgName: "",
	designation: "",
	relationWithOwner: null,
	otherRelationWithOwner: "",
	email: "",
	phone: "",
	personalName: "",
	address: "",
	landmark: "",
	city: "",
	pin: "",
	country: "",
	captcha: null,
	herebyCertifyPoints: [
		"I have a good faith belief that this complaint is true and correct and that I have taken all reasonable measures to verify my claim",
		"The complaint is valid in law and does not meet any of the exceptions set out under Indian law",
		"I have the power and authority to make this claim",
		"My statements are correct",
		"The claims made under this complaint are not frivolous in nature and do not cause harassment to any individual/organisation"
	],
	onBehalf: ""
};
export default {
	components: {
		MediaPreview,
		BasicLayout,
		ClaimTitle,
		VueRecaptcha
	},
	data: () => ({
		siteKey: config.RECAPTCHA_SITE_KEY,
		claimItems: [
			{
				text: "This is an unauthorised use of my name/image/trademark",
				value: 1
			},
			{ text: "This is a copyright/trademark infringement", value: 2 },
			{ text: "Other legal issue", value: 3 }
		],
		affectedWho: [
			{ text: "Me", value: 1 },
			{
				text: "My company, organisation, client (if you are a lawyer/agent/representative)",
				value: 2
			},
			{ text: "Another copyright owner", value: 3 },
			{ text: "Other", value: 4 }
		],
		unauthorisedUseReasons: [
			{ text: "A trademark/name", value: 1 },
			{ text: "Art, literary work", value: 2 },
			{ text: "Video/audio/sound recording", value: 3 },
			{
				text: "A social media post made on ShareChat on another platform",
				value: 4
			},
			{ text: "A recording of a live performance", value: 5 },
			{ text: "Software", value: 6 },
			{ text: "Other", value: 7 }
		],
		abilityHamperReasons: [
			{
				text: "Reproduce in any material form including the storing of it by electronic means",
				value: 1
			},
			{ text: "Make copies to the public", value: 2 },
			{
				text: "Perform in public, or communicate to the public",
				value: 3
			},
			{ text: "Record/translate/adapt the work", value: 4 },
			{ text: "Sell, rent the work", value: 5 },
			{ text: "Other", value: 6 }
		],
		relationWithOwners: [
			{ text: "Me", value: 1 },
			{ text: "Other", value: 2 }
		],
		loading: false,
		files: {},
		...initialData
	}),
	computed: {
		linkErrors() {
			return this.links.reduce((acc, link) => {
				const error = findErrorInSharechatLink(link);
				if (error) {
					return [...acc, error];
				}
				return acc;
			}, []);
		},
		// Tracks completion of each of the 7 form sections (index 0–6)
		sectionComplete() {
			const hasLinks = this.links.length > 0 && !this.linkErrors.length;
			return [
				// 1. Claim
				this.claimType !== null &&
					(this.claimType !== 3 || !!this.otherClaimType.trim()),
				// 2. Links
				hasLinks,
				// 3. Impact & harm
				(this.affectedPerson !== 3 && this.affectedPerson !== 4
					? true
					: !!this.otherAffectedPerson.trim()) &&
					(this.unauthorisedUseReason !== 7 || !!this.otherUnauthorisedUseReason.trim()) &&
					(this.abilityHamperReason !== 6 || !!this.otherAbilityHamperReason.trim()),
				// 4. Who is filing
				!!this.orgName.trim() &&
					!!this.relationWithOwner &&
					(this.relationWithOwner !== 2 || !!this.otherRelationWithOwner.trim()),
				// 5. Contact
				!!this.email.trim() &&
					!!this.personalName.trim() &&
					!!this.address.trim() &&
					!!this.city.trim() &&
					!!this.pin.trim() &&
					!!this.country.trim(),
				// 6. Supporting files (signature required)
				!!(this.files.signature || []).length,
				// 7. Declaration — always passes (read-only)
				true
			];
		},
		completedCount() {
			return this.sectionComplete.filter(Boolean).length;
		},
		// Per-field format errors, keyed by data property. Empty fields report no
		// error (required-ness is handled by `disabled`), so pristine fields stay clean.
		fieldErrors() {
			return Object.keys(claimFieldValidators).reduce((acc, key) => {
				const value = (this[key] || "").trim();
				const error = value ? claimFieldValidators[key](value) : null;
				acc[key] = error ? [error] : [];
				return acc;
			}, {});
		},
		hasFieldErrors() {
			return Object.values(this.fieldErrors).some(errors => errors.length);
		},
		audioUploadData(state) {
			return state.media[this.audioKey] || {};
		},
		disabled() {
			if (this.hasFieldErrors) {
				return true;
			}
			if (this.claimType === 3 && !this.otherClaimType.trim()) {
				return true;
			}
			if (this.links && !this.links.length) {
				return true;
			}

			if (this.links.length && !!this.linkErrors.length) {
				return true;
			}

			if ((this.affectedPerson === 3 || this.affectedPerson === 4) && !this.otherAffectedPerson.trim()) {
				return true;
			}
			if (this.unauthorisedUseReason === 7 && !this.otherUnauthorisedUseReason.trim()) {
				return true;
			}
			if (this.abilityHamperReason === 6 && !this.otherAbilityHamperReason.trim()) {
				return true;
			}
			if (!this.relationWithOwner) {
				return true;
			}
			if (this.relationWithOwner === 2 && !this.otherRelationWithOwner.trim()) {
				return true;
			}

			if (this.files && !(this.files.signature || []).length) {
				return true;
			}

			return !(
				this.orgName.trim() &&
				this.email.trim() &&
				this.pin.trim() &&
				this.address.trim() &&
				this.city.trim() &&
				this.country.trim() &&
				this.personalName.trim() &&
				!!this.captcha
			);
		}
	},
	methods: {
		onFileChange(files, key) {
			this.files = {
				...this.files,
				[key]: files
			};
		},
		getKeyWithOtherValue(allValues, value, otherNumbers, otherValue) {
			const text = allValues.find(valueObj => valueObj.value === value).text;
			return otherNumbers.includes(value) ? `${text} ${otherValue}` : text;
		},
		onSubmitClick() {
			this.loading = true;
			this.doUpload()
				.then(urlMap => {
					return urlMap.reduce((acc, [key, filename]) => {
						acc[key] = [...(acc[key] || []), filename];
						return acc;
					}, {});
				})
				.then(data => {
					const payload = {
						links: this.links,
						claimType: this.getKeyWithOtherValue(this.claimItems, this.claimType, [3], this.otherClaimType),
						affectedPerson: this.getKeyWithOtherValue(
							this.affectedWho,
							this.affectedPerson,
							[3, 4],
							this.otherAffectedPerson
						),
						unauthorisedUseReason: this.getKeyWithOtherValue(
							this.unauthorisedUseReasons,
							this.unauthorisedUseReason,
							[7],
							this.otherUnauthorisedUseReason
						),
						abilityHamperReason: this.getKeyWithOtherValue(
							this.abilityHamperReasons,
							this.abilityHamperReason,
							[6],
							this.otherAbilityHamperReason
						),
						otherInfringementReason: this.otherInfringementReason,
						orgName: this.orgName,
						designation: this.designation,
						relationWithOwner: this.getKeyWithOtherValue(
							this.relationWithOwners,
							this.relationWithOwner,
							[2],
							this.otherRelationWithOwner
						),
						email: this.email,
						phone: this.phone,
						personalName: this.personalName,
						address: this.address,
						landmark: this.landmark,
						city: this.city,
						pin: this.pin,
						country: this.country,
						captcha: this.captcha,
						...data,
						signature: data.signature[0],
						onBehalf: this.onBehalf
					};
					return this.submitCopyRightClaim(this.removeEmpty(payload));
				})
				.then(() => {
					Object.assign(this, initialData);
					this.files = {};
					this.$refs["relationProofs"].files = [];
					this.$refs["signature"].files = [];
					this.$refs["otherFiles"].files = [];
				})
				.catch(err => {
					console.error(err);
				})
				.then(() => {
					this.resetRecaptcha();
					this.loading = false;
				});
		},
		submitCopyRightClaim(payload) {
			return this.$store.dispatch("SUBMIT_COPYRIGHT_CLAIM", payload);
		},
		removeEmpty(payload) {
			return Object.keys(payload).reduce((acc, curr) => {
				let value = payload[curr];
				if ((Array.isArray(value) && value.length === 0) || !value) {
					return acc;
				}
				return {
					...acc,
					[curr]: payload[curr]
				};
			}, {});
		},
		uploadMedia(file) {
			let formData = new FormData();
			formData.append("file", file);
			return this.$store.dispatch("UPLOAD_MEDIA", formData);
		},

		doUpload() {
			const uploads = [];
			const keys = Object.keys(this.files);

			keys.forEach(key => {
				(this.files[key] || []).forEach(file => {
					uploads.push([key, file]);
				});
			});
			return Promise.all(
				uploads.map(([key, file]) => this.uploadMedia(file).then(({ filename }) => [key, filename]))
			);
		},
		verifyUser(payload) {
			this.captcha = payload;
		},
		onExpired() {
			this.resetRecaptcha();
		},
		resetRecaptcha() {
			this.$refs.recaptcha.reset();
			this.captcha = null;
		}
	},
	watch: {
		links(newVal) {
			if (newVal) {
				const hasSpacesOrCommas = newVal.some(link => link.match(/,|\s/));
				if (hasSpacesOrCommas) {
					this.links = [
						...new Set(
							this.links.reduce((acc, link) => {
								const newLinks = link.split(/,|\s+/).filter(link => Boolean(link.trim()));
								return [...acc, ...newLinks];
							}, [])
						)
					];
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.copyright-form-root {
	max-width: 720px;
	margin: 0 auto 3rem;
	padding-bottom: 2rem;
}

/* ─── Hero ─────────────────────────────────────────────── */
.form-hero {
	padding: 2rem 0 2.75rem;
}

.form-hero__title {
	font-size: 2rem;
	font-weight: 800;
	letter-spacing: -0.03em;
	line-height: 1.1;
	color: rgba(0, 0, 0, 0.88);
	margin: 0 0 0.9rem;
}

@media (min-width: 600px) {
	.form-hero__title {
		font-size: 2.625rem;
	}
}

.form-hero__lead {
	font-size: 1rem;
	line-height: 1.65;
	color: rgba(0, 0, 0, 0.5);
	max-width: 34rem;
	margin: 0 auto 1.5rem;
}

.form-hero__chips {
	gap: 0.5rem;
	margin-bottom: 1.5rem;
}

.form-hero__chip {
	display: inline-flex;
	align-items: center;
	font-size: 0.8125rem;
	font-weight: 500;
	color: rgba(0, 0, 0, 0.62);
	padding: 0.3rem 0.875rem;
	border-radius: 999px;
	background: #ffffff;
	border: 1px solid rgba(0, 0, 0, 0.1);
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	transition: box-shadow 0.15s ease, border-color 0.15s ease, color 0.15s ease;

	&:hover {
		box-shadow: 0 2px 10px rgba(76, 175, 80, 0.15);
		border-color: rgba(76, 175, 80, 0.4);
		color: #2e7d32;
	}
}

/* ─── Progress tracker ───────────────────────────────── */
.form-hero__progress-wrap {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
}

.form-hero__steps {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 0.375rem;
}

.form-hero__step-dot {
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background: rgba(0, 0, 0, 0.07);
	border: 2px solid rgba(0, 0, 0, 0.1);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition: background 0.3s ease, border-color 0.3s ease, transform 0.25s ease,
		box-shadow 0.25s ease;

	/* active = next section user should fill */
	&--active {
		background: rgba(76, 175, 80, 0.12);
		border-color: #4caf50;
		transform: scale(1.15);
		box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.15);
	}

	/* done = section fully complete */
	&--done {
		background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
		border-color: #4caf50;
		box-shadow: 0 2px 6px rgba(76, 175, 80, 0.4);
	}
}

.form-hero__progress-label {
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.02em;
	color: rgba(0, 0, 0, 0.4);
	margin: 0;
	transition: color 0.3s ease;
}

/* ─── Section connectors ──────────────────────────────── */
.section-connector {
	position: relative;
	width: 2px;
	height: 28px;
	background: rgba(0, 0, 0, 0.08);
	margin: 0 auto;
	border-radius: 1px;
	overflow: hidden;

	/* filled bar that grows from top when section above is complete */
	&::after {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: linear-gradient(to bottom, #4caf50, #66bb6a);
		border-radius: 1px;
		transform: scaleY(0);
		transform-origin: top;
		transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
	}

	&--done::after {
		transform: scaleY(1);
	}
}

/* ─── Section cards ──────────────────────────────────── */
.copyright-section {
	border-radius: 16px !important;
	padding: 1.5rem 1.25rem 1.75rem;
	margin-bottom: 0;
	border-color: rgba(0, 0, 0, 0.07) !important;
	background: #ffffff !important;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.04) !important;
	transition: box-shadow 0.22s ease, border-color 0.22s ease !important;
	position: relative;
	overflow: hidden;

	/* left accent bar */
	&::before {
		content: '';
		position: absolute;
		left: 0;
		top: 0;
		bottom: 0;
		width: 3px;
		background: linear-gradient(to bottom, #4caf50 0%, rgba(76, 175, 80, 0.15) 100%);
		border-radius: 16px 0 0 16px;
		opacity: 0;
		transition: opacity 0.22s ease;
	}

	&:hover {
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05), 0 10px 28px rgba(0, 0, 0, 0.08) !important;
		border-color: rgba(76, 175, 80, 0.18) !important;

		&::before {
			opacity: 1;
		}
	}
}

@media (min-width: 600px) {
	.copyright-section {
		padding: 1.75rem 2rem 2rem;
	}
}

.copyright-section--legal {
	background: linear-gradient(160deg, rgba(248, 249, 251, 0.9) 0%, #ffffff 100%) !important;
}

/* ─── Section heading ────────────────────────────────── */
.section-heading {
	display: flex;
	align-items: center;
	gap: 0.875rem;
	margin-bottom: 0.5rem;
}

.section-heading__badge {
	flex-shrink: 0;
	width: 2.125rem;
	height: 2.125rem;
	border-radius: 10px;
	background: linear-gradient(145deg, #4caf50 0%, #66bb6a 100%);
	color: #fff;
	font-weight: 700;
	font-size: 0.875rem;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 3px 10px rgba(76, 175, 80, 0.35);
	letter-spacing: 0;
}

.section-heading__badge--muted {
	background: linear-gradient(145deg, #6b7280 0%, #9ca3af 100%);
	box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

.section-heading__title {
	font-size: 1.125rem;
	font-weight: 700;
	letter-spacing: -0.015em;
	margin: 0 0 0.2rem;
	color: rgba(0, 0, 0, 0.88);
}

.section-heading__desc {
	font-size: 0.8125rem;
	line-height: 1.5;
	color: rgba(0, 0, 0, 0.46);
	margin: 0;
}

.subsection-label {
	font-size: 0.9375rem;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.72);
	letter-spacing: -0.005em;
	padding-left: 0.5rem;
	border-left: 3px solid rgba(76, 175, 80, 0.5);
}

/* Vertical gap between wrapped rows in form field grids */
.copyright-section ::v-deep .layout.wrap {
	row-gap: 1rem;
}

.link-error-banner {
	display: flex;
	align-items: flex-start;
	padding: 0.75rem 1rem;
	border-radius: 10px;
	background: rgba(220, 38, 38, 0.05);
	border: 1px solid rgba(220, 38, 38, 0.2);
	color: #b91c1c;
	font-size: 0.8125rem;
	line-height: 1.5;
}

.certify-list {
	list-style: none;
	padding: 0;
	margin: 0;
}

.certify-list__item {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	padding: 0.875rem 0.75rem;
	font-size: 0.875rem;
	line-height: 1.6;
	color: rgba(0, 0, 0, 0.68);
	border-bottom: 1px solid rgba(0, 0, 0, 0.05);
	border-radius: 8px;
	transition: background 0.15s ease, color 0.15s ease;

	&:hover {
		background: rgba(76, 175, 80, 0.04);
		color: rgba(0, 0, 0, 0.78);
	}

	&:last-child {
		border-bottom: none;
		padding-bottom: 0.5rem;
	}

	&:first-child {
		padding-top: 0.5rem;
	}
}

/* filled check badge for certify items */
.certify-list__check {
	flex-shrink: 0;
	width: 0.4rem;
	height: 0.4rem;
	border-radius: 50%;
	background: rgba(0, 0, 0, 0.7);
	margin-top: 0.55rem;
}

.copyright-submit-panel {
	border-radius: 16px !important;
	padding: 1.5rem 1.25rem 1.75rem;
	margin-top: 1rem;
	border-color: rgba(76, 175, 80, 0.25) !important;
	background: linear-gradient(145deg, rgba(76, 175, 80, 0.07) 0%, rgba(76, 175, 80, 0.02) 40%, #ffffff 100%) !important;
	box-shadow: 0 4px 28px rgba(76, 175, 80, 0.12), 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

@media (min-width: 600px) {
	.copyright-submit-panel {
		padding: 2rem 2rem 2rem;
	}
}

.copyright-submit-panel__header {
	display: flex;
	align-items: flex-start;
	gap: 0.625rem;
	margin-bottom: 1.25rem;
}

.copyright-submit-panel__icon {
	flex-shrink: 0;
	width: 2rem;
	height: 2rem;
	border-radius: 8px;
	background: rgba(76, 175, 80, 0.12);
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 1px;
}

.copyright-submit-panel__note {
	font-size: 0.8125rem;
	line-height: 1.55;
	color: rgba(0, 0, 0, 0.5);
	margin: 0;
	padding-top: 0.25rem;
}

/* ─── Submit button (native <button>, no Vuetify) ─────── */
.copyright-submit-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.375rem;
	width: 100%;
	min-height: 56px;
	padding: 0 1.5rem;
	border: none;
	border-radius: 14px;
	font-family: inherit;
	font-size: 1rem;
	font-weight: 700;
	letter-spacing: 0.02em;
	color: #ffffff;
	cursor: pointer;
	position: relative;
	overflow: hidden;
	/* enabled state: vivid gradient */
	background: linear-gradient(135deg, #43a047 0%, #66bb6a 100%);
	box-shadow: 0 6px 24px rgba(76, 175, 80, 0.45), 0 2px 8px rgba(76, 175, 80, 0.2),
		inset 0 1px 0 rgba(255, 255, 255, 0.2);
	transition: transform 0.18s ease, box-shadow 0.2s ease, background 0.2s ease;

	/* shimmer sweep on hover */
	&::before {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			105deg,
			transparent 40%,
			rgba(255, 255, 255, 0.18) 50%,
			transparent 60%
		);
		transform: translateX(-100%);
		transition: transform 0.45s ease;
	}

	&:hover:not(:disabled) {
		transform: translateY(-2px);
		box-shadow: 0 12px 36px rgba(76, 175, 80, 0.55), 0 4px 12px rgba(76, 175, 80, 0.25),
			inset 0 1px 0 rgba(255, 255, 255, 0.25);

		&::before {
			transform: translateX(100%);
		}
	}

	&:active:not(:disabled) {
		transform: translateY(0);
		box-shadow: 0 3px 12px rgba(76, 175, 80, 0.35);
	}

	/* disabled state: muted, clearly non-interactive */
	&--disabled,
	&:disabled {
		background: linear-gradient(135deg, #e0e0e0 0%, #ebebeb 100%);
		color: rgba(0, 0, 0, 0.3);
		box-shadow: none;
		cursor: not-allowed;
		transform: none;

		&::before {
			display: none;
		}
	}

	/* loading state */
	&--loading {
		pointer-events: none;
		opacity: 0.85;
	}
}

/* CSS spinner for loading state */
.copyright-submit-btn__spinner {
	width: 18px;
	height: 18px;
	border: 2.5px solid rgba(255, 255, 255, 0.35);
	border-top-color: #ffffff;
	border-radius: 50%;
	animation: spin 0.75s linear infinite;
	flex-shrink: 0;
}

@keyframes spin {
	to { transform: rotate(360deg); }
}

.copyright-recaptcha {
	display: flex;
	justify-content: flex-start;
	margin-bottom: 1.25rem;
}

/* Softer outlined fields with consistent border-radius */
.copyright-form-root ::v-deep .v-text-field--outlined fieldset,
.copyright-form-root ::v-deep .v-select--outlined fieldset,
.copyright-form-root ::v-deep .v-autocomplete--outlined fieldset,
.copyright-form-root ::v-deep .v-combobox--outlined fieldset {
	border-radius: 10px;
	border-color: rgba(0, 0, 0, 0.11) !important;
	transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

/* Subtle inner shadow on all inputs for depth */
.copyright-form-root ::v-deep .v-text-field--outlined .v-input__control,
.copyright-form-root ::v-deep .v-select--outlined .v-input__control,
.copyright-form-root ::v-deep .v-autocomplete--outlined .v-input__control,
.copyright-form-root ::v-deep .v-combobox--outlined .v-input__control {
	border-radius: 10px;
}

.copyright-form-root ::v-deep .v-text-field--outlined:hover fieldset,
.copyright-form-root ::v-deep .v-select--outlined:hover fieldset,
.copyright-form-root ::v-deep .v-autocomplete--outlined:hover fieldset,
.copyright-form-root ::v-deep .v-combobox--outlined:hover fieldset {
	border-color: rgba(0, 0, 0, 0.22) !important;
}

.copyright-form-root ::v-deep .v-text-field--outlined.v-input--is-focused fieldset,
.copyright-form-root ::v-deep .v-select--outlined.v-input--is-focused fieldset,
.copyright-form-root ::v-deep .v-autocomplete--outlined.v-input--is-focused fieldset,
.copyright-form-root ::v-deep .v-combobox--outlined.v-input--is-focused fieldset {
	border-color: #4caf50 !important;
	border-width: 2px;
	box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.12);
}

/* Input label color on focus */
.copyright-form-root ::v-deep .v-input--is-focused .v-label {
	color: #4caf50 !important;
}

/* Chip styling in comboboxes */
.copyright-form-root ::v-deep .v-chip {
	border-radius: 7px !important;
	font-size: 0.8125rem !important;
	font-weight: 500 !important;
	background: rgba(76, 175, 80, 0.1) !important;
	color: #2e7d32 !important;
	border: 1px solid rgba(76, 175, 80, 0.22) !important;
}

/* Remove button in outlined inputs */
.copyright-form-root ::v-deep .v-input__icon--clear .v-icon {
	font-size: 18px !important;
}

/* Cap the URL combobox height so it doesn't blow the layout */
.content-links-field ::v-deep .v-select__selections {
	max-height: 160px;
	overflow-y: auto;
	padding: 6px 0 4px;
	row-gap: 4px;
	scrollbar-width: thin;
	scrollbar-color: rgba(0, 0, 0, 0.15) transparent;

	&::-webkit-scrollbar {
		width: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: rgba(0, 0, 0, 0.15);
		border-radius: 4px;
	}
}

/* Make chips in the URL field slightly more compact */
.content-links-field ::v-deep .v-chip {
	margin: 2px 4px 2px 0 !important;
}

/* Ensure the input inside the URL combobox doesn't push height when empty */
.content-links-field ::v-deep .v-select__selections input {
	min-height: 28px;
}
</style>
